import React, { useEffect, useState } from "react";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import ReadOnlyProtectedForm from "../../Inputs/ReadOnlyProtectedForm";
import Skeleton from "@mui/material/Skeleton";
import { useSharedSelector } from "../../../store/SharedProvider";
import { selectDistanceUnit } from "../../../store/app-configurations/app-configurations-selectors";
import { DistanceUnit, DistanceValueUnit } from "../../../enums/configuration/units";
import useDistanceUnitValue from "../../../hooks/useDistanceUnitValue";
import { getLimitedValue } from "../../../utils/util_functions";

const NavigatorOptionConfig: React.FC<{
  label: string;
  loading?: boolean;
  value: number | undefined;
  onChange: (value: number) => void;
  sliderOptions?: { step: number; min: number; max: number };
}> = ({ label, loading, value, onChange, sliderOptions }) => {
  const { t } = useTranslation();

  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));
  const { multiplier } = useDistanceUnitValue(0, DistanceValueUnit.METERS);


  const convertValueForState = (value: number) => {
    if (distanceUnit === DistanceUnit.IMPERIAL) {
      return Math.round(value * multiplier * 10) / 10;
    }
    return value;
  };

  const [stateValue, setStateValue] = useState(convertValueForState(value ?? 0));

  const sliderStateChangeHandler = (newValue: number | undefined) => {
    if (newValue != null) {
      setStateValue(newValue);
    }
  };

  useEffect(() => {
    setStateValue(convertValueForState(value ?? 0));
  }, [value]);

  const convertValueToM = (value: number) => {
    if (distanceUnit === DistanceUnit.METRIC) {
      return value;
    }
    return value / multiplier;
  };

  return (
    <ReadOnlyProtectedForm>
      <InputLabel id={`navigator-settings-${label}`}>
        {loading ? (
          <Skeleton />
        ) : (
          t(`navigator-settings-${label}-${distanceUnit === DistanceUnit.METRIC ? "metric" : "imperial"}`)
        )}
      </InputLabel>
      {loading ? (
        <Skeleton variant="rectangular" sx={(theme) => ({ width: "100%", height: theme.spacing(4) })} />
      ) : (
        <Stack direction="row" gap={2} alignItems="center">
          <Slider
            value={stateValue}
            onChange={(_: Event, value: number | number[], __: number) => sliderStateChangeHandler(value as number)}
            min={convertValueForState(sliderOptions?.min ?? 0)}
            max={convertValueForState(sliderOptions?.max ?? 0)}
            step={convertValueForState(sliderOptions?.step ?? 0.25)}
            valueLabelDisplay="off"
            onChangeCommitted={stateValue != null ? () => onChange(convertValueToM(stateValue)) : undefined}
          />
          <Input
            value={stateValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange(
                convertValueToM(
                  sliderOptions != null
                    ? getLimitedValue(+event.target.value, sliderOptions.min, sliderOptions.max)
                    : +event.target.value
                )
              )
            }
            type="number"
            sx={(theme) => ({ width: theme.spacing(12) })}
          />
        </Stack>
      )}
    </ReadOnlyProtectedForm>
  );
};

export default NavigatorOptionConfig;
